/**
 * Storage Section for Recipe Import Preview
 *
 * Displays storage-related fields for editing extracted recipe data.
 */

import type { ExtractedRecipe } from '@/types/recipeImport';

interface StorageSectionProps {
  recipe: ExtractedRecipe;
  onChange: (recipe: ExtractedRecipe) => void;
}

export function StorageSection({ recipe, onChange }: StorageSectionProps) {
  return (
    <div className="grid grid-cols-2 gap-4">
      {/* Toggles */}
      <div className="col-span-2 flex flex-wrap gap-6">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={recipe.eat_immediately}
            onChange={(e) => onChange({ ...recipe, eat_immediately: e.target.checked })}
            className="w-[18px] h-[18px] rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700">
            Eat immediately after preparation
          </span>
        </label>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={recipe.freezable}
            onChange={(e) => onChange({ ...recipe, freezable: e.target.checked })}
            className="w-[18px] h-[18px] rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700">Can be frozen</span>
        </label>
      </div>

      {/* Fridge life */}
      <div className="flex flex-col gap-1.5">
        <label className="text-sm font-medium text-gray-700">
          Fridge Life <span className="font-normal text-gray-400">(optional)</span>
        </label>
        <div className="flex items-center gap-2">
          <input
            type="number"
            value={recipe.fridge_life_days ?? ''}
            onChange={(e) =>
              onChange({
                ...recipe,
                fridge_life_days: e.target.value ? Number(e.target.value) : null,
              })
            }
            placeholder="3"
            min="0"
            className="w-20 px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
          />
          <span className="text-sm text-gray-600">days</span>
        </div>
      </div>

      {/* Healthiness rating */}
      <div className="flex flex-col gap-1.5">
        <label className="text-sm font-medium text-gray-700">
          Healthiness Rating <span className="font-normal text-gray-400">(optional)</span>
        </label>
        <div className="flex gap-2">
          {[1, 2, 3, 4, 5].map((rating) => (
            <button
              key={rating}
              type="button"
              onClick={() =>
                onChange({
                  ...recipe,
                  healthiness_rating: recipe.healthiness_rating === rating ? null : rating,
                })
              }
              className={`w-10 h-10 border-2 rounded-lg flex items-center justify-center text-lg font-medium transition-colors ${
                recipe.healthiness_rating === rating
                  ? 'border-amber-400 bg-amber-100 text-amber-700'
                  : 'border-gray-200 hover:border-amber-400 hover:bg-amber-50'
              }`}
            >
              {rating}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
